import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useAppSelector } from "../hooks/storeHook";
import ChatApi from "../apis/chatApi";

type Status = "loading" | "success" | "error";

const parseExportParams = (raw: string): { actionName: string; paramsObj: Record<string, unknown> } | null => {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length < 2) return null;
    const actionName = typeof parsed[0] === "string" ? parsed[0].trim() : "";
    if (!actionName) return null;
    const rawParams = parsed[1];
    const paramsObj =
      rawParams && typeof rawParams === "object" && !Array.isArray(rawParams)
        ? (rawParams as Record<string, unknown>)
        : {};
    return { actionName, paramsObj };
  } catch {
    return null;
  }
};

const ExportResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth0();
  const aiThreadId = useAppSelector((state: any) => state.chat?.aiThreadId);
  const selectedUserThreadId = useAppSelector((state: any) => state.chat?.selectedUserThreadId);
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState("Preparing export…");

  const threadId = useMemo(() => {
    return String(aiThreadId ?? selectedUserThreadId ?? "").trim() || null;
  }, [aiThreadId, selectedUserThreadId]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        const parsed = parseExportParams(searchParams.get("params") || "");
        if (!parsed) {
          throw new Error("Invalid export link payload.");
        }
        if (!threadId) {
          throw new Error("Export unavailable: missing thread context");
        }
        const userEmail = String(user?.email ?? "").trim();
        if (!userEmail) {
          throw new Error("Export unavailable: missing user context");
        }

        const api = new ChatApi();
        await api.handleExportAction(threadId, userEmail, parsed.actionName, parsed.paramsObj || {});
        if (!mounted) return;
        setStatus("success");
        setMessage("Export request sent. Return to the chat thread for status and link.");
      } catch (error: any) {
        if (!mounted) return;
        setStatus("error");
        setMessage(error?.message || "Failed to start export.");
      }
    };

    void run();
    return () => {
      mounted = false;
    };
  }, [searchParams, threadId, user?.email]);

  return (
    <Box sx={{ width: "100%", minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
      <Paper elevation={1} sx={{ width: "100%", maxWidth: 680, p: 3, borderRadius: 2 }}>
        {status === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontFamily: "var(--theo-font-sans)" }}>Preparing export…</Typography>
          </Box>
        )}
        {status === "success" && (
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#1b5e20" }}>{message}</Typography>
        )}
        {status === "error" && (
          <Box>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#b3261e", fontWeight: 600, mb: 0.5 }}>
              Export failed
            </Typography>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#444" }}>{message}</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ExportResults;
